import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Gift, Copy, Check, ArrowLeft } from 'lucide-react';
import { useUserClubs } from '../../hooks/useStoreApi';
import { Modal } from '../ui/Modal';
import { navigate as appNavigate } from '../../App';

/**
 * Homepage widget tile for the user's customers-club referral coupons.
 * Matches the other home tiles: a compact card showing just the coupon count;
 * clicking opens a modal with the full per-store details (code, usage,
 * accumulated discount). Renders nothing when the user has no referral coupons.
 */
export default function MyClubCoupons({ chatId, navigate }) {
    const go = navigate || appNavigate;
    const { data } = useUserClubs(chatId);
    const coupons = data?.coupons || [];
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(null);

    if (!coupons.length) return null;

    const copy = async (code) => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(code);
            setTimeout(() => setCopied(null), 2000);
        } catch { /* clipboard unavailable */ }
    };

    return (
        <>
            {/* Compact tile — same shape/behaviour as the other homepage widgets */}
            <motion.button
                type="button"
                onClick={() => setOpen(true)}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 320, damping: 22 }}
                className="group relative text-right overflow-hidden rounded-2xl bg-surface
                    border border-line-subtle hover:border-line shadow-card hover:shadow-elevated
                    transition-shadow duration-300 p-3.5 sm:p-4 flex flex-col
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-success-400"
                aria-label="קופוני ההפניה שלי"
            >
                <div aria-hidden="true" className="pointer-events-none absolute -top-10 -start-8 w-24 h-24 rounded-pill blur-2xl opacity-60 bg-success-300/40" />

                <div className="relative flex items-center justify-between mb-2">
                    <div className="inline-flex items-center justify-center w-9 h-9 rounded-xl shadow-sm text-white bg-gradient-to-br from-success-500 to-success-600">
                        <Gift size={18} strokeWidth={2.25} />
                    </div>
                    <span className="text-2xl sm:text-3xl font-bold text-ink-strong leading-none tabular-nums">
                        {coupons.length}
                    </span>
                </div>

                <div className="relative">
                    <h3 className="text-sm sm:text-base font-bold text-ink-strong leading-tight">קופוני הפניה</h3>
                    <p className="text-2xs sm:text-xs text-ink-muted mt-0.5 leading-snug line-clamp-1">ההטבות שלי במועדוני לקוחות</p>
                </div>
            </motion.button>

            {/* Full details modal */}
            <Modal isOpen={open} onClose={() => setOpen(false)} title="קופוני ההפניה שלי" size="md">
                <div className="space-y-3">
                    {coupons.map((c) => (
                        <div key={c.store_id} className="bg-surface rounded-xl border border-line-subtle shadow-sm p-3.5">
                            <div className="flex items-center gap-3">
                                <div className="relative w-12 h-12 rounded-lg overflow-hidden bg-gradient-to-br from-brand-400 to-brand-500 shrink-0 flex items-center justify-center">
                                    <Gift size={20} className="text-white" strokeWidth={2} />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <h3 className="text-sm font-bold text-ink-strong line-clamp-1">{c.store_name || 'החנות'}</h3>
                                    <div className="flex items-center gap-1.5 mt-1">
                                        <span className="font-mono text-sm font-bold text-ink bg-surface-muted px-2 py-0.5 rounded-md border border-line" dir="ltr">{c.code}</span>
                                        <button
                                            type="button"
                                            onClick={() => copy(c.code)}
                                            aria-label="העתק קוד"
                                            className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-line bg-surface hover:bg-surface-muted text-ink-muted transition-colors"
                                        >
                                            {copied === c.code ? <Check className="w-4 h-4 text-success-600" /> : <Copy className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => go('store', `slug=${c.store_slug}`)}
                                    className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-brand-600 hover:text-brand-700"
                                >
                                    לחנות <ArrowLeft className="w-3.5 h-3.5" />
                                </button>
                            </div>

                            <div className="mt-3 pt-3 border-t border-line-subtle grid grid-cols-2 gap-2">
                                <div>
                                    <p className="text-2xs text-ink-subtle">חברים שמימשו</p>
                                    <p className="text-sm font-bold text-ink-strong tabular-nums">{c.uses}/{c.max_uses}</p>
                                </div>
                                <div>
                                    <p className="text-2xs text-ink-subtle">הנחה צבורה</p>
                                    <p className="text-sm font-bold text-success-600 tabular-nums">
                                        {c.credit_percent}%
                                        <span className="text-2xs font-normal text-ink-subtle"> / {c.max_credit_percent}%</span>
                                    </p>
                                </div>
                            </div>

                            <p className="mt-2 text-2xs sm:text-xs text-ink-subtle leading-snug">
                                {c.credit_percent > 0
                                    ? 'הזינו את הקוד בתשלום כדי לממש את ההנחה הצבורה.'
                                    : `שתפו את הקוד — כל חבר שיממש מזכה אתכם ב-${c.per_referral_percent}%.`}
                            </p>
                        </div>
                    ))}
                </div>
            </Modal>
        </>
    );
}
